const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

export type TimeInput = string | number | Date;

function toDate(value: TimeInput): Date {
  return value instanceof Date ? value : new Date(value);
}

function ago(amount: number, unit: string): string {
  return `${amount} ${unit}${amount === 1 ? '' : 's'} ago`;
}

export function formatRelativeTime(
  value: TimeInput,
  now: TimeInput = new Date(),
): string {
  const diff = toDate(now).getTime() - toDate(value).getTime();

  if (Number.isNaN(diff)) return '';
  if (diff < MINUTE) return 'just now';
  if (diff < HOUR) return ago(Math.round(diff / MINUTE), 'minute');
  if (diff < DAY) return ago(Math.round(diff / HOUR), 'hour');
  if (diff < WEEK) return ago(Math.round(diff / DAY), 'day');
  return ago(Math.round(diff / WEEK), 'week');
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// "Sept" matches the design's "24 Sept"; the rest are the usual 3-letter forms
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec',
];

// Calendar-day start in the viewer's local time, so "Today"/"Yesterday" track
// the local date rather than a UTC boundary.
const startOfDay = (date: Date): number =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

// Short review date: Today / Yesterday / weekday within the last week / "24 Sept".
export function formatReviewDate(
  value: TimeInput,
  now: TimeInput = new Date(),
): string {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return '';
  const days = Math.round((startOfDay(toDate(now)) - startOfDay(date)) / DAY);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return WEEKDAYS[date.getDay()];
  return `${date.getDate()} ${MONTHS[date.getMonth()]}`;
}
